import React from 'react';
import { Spacing } from '../../../../../components/entities-card/Spacing';
import { LegislationCardTitleLink } from '../../../../../legislation/card/LegislationCardTitleLink';
import { EntitiesCardBody } from '../../../../../components/entities-card/EntitiesCardBody';
import { DecisionQpc } from '../../../../../decision/legislation-snippet/qpc/DecisionQpc';
import { DecisionReferences } from '../../../../../decision/legislation-snippet/references/DecisionReferences';
import { doctrineAnalytics } from '../../../../../utils/analyticsUtils';
import { buildUrlWithParameters } from '../../../../../utils/url-utils/urlUtils';
import { fetchLawCardReferenceDecisions } from './searchSnippetLegislation.api';
import { LegislationContent } from './LegislationContent';
import { LegislationRestrictor } from './LegislationRestrictor';
import { LegislationSelector } from './LegislationSelector';
import { Connect, PropsFromRedux } from './LegislationSnippetContainer';
import { makeLawEventData } from './legislationSnippetUtils';
import {
  withRestrictedModal,
  WithRestrictedModalProps,
} from '../../../../../components/modal/components/modal-restricted/withRestrictedModal';
import { UserContextType } from '../../../../../user/user.types';
import { withUser } from '../../../../../user/UserContext';
import { StateType } from '../../../../../state/state.types';
import { DecisionReferenceInSnippet } from '../../../../../decision/decision.types';

/* eslint-disable camelcase */
interface CustomWindow extends Window {
  original_query?: {
    source: string;
  };
}
/* eslint-enable camelcase */

declare let window: CustomWindow;

type Props = {
  legislation: NonNullable<StateType['results']['snippets']['law']>;
  originalQueryKey?: string;
} & UserContextType &
  PropsFromRedux &
  PropsFromRedux &
  WithRestrictedModalProps;

type State = {
  decisionsReferences: Array<DecisionReferenceInSnippet>;
};

class RawLegislationSnippet extends React.Component<Props, State> {
  state: State = {
    decisionsReferences: [],
  };

  componentDidMount() {
    const { legislation } = this.props;
    const lawForAnalytics = {
      ...makeLawEventData(legislation),
      source: window.original_query ? window.original_query.source : 'direct',
    };

    doctrineAnalytics('Search LawDisplayed', lawForAnalytics);
    this.loadDecisionReferences();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.legislation.articleId !== this.props.legislation.articleId) {
      this.loadDecisionReferences();
    }
  }

  onTitleClick = () => {
    const { accessPremium, legislation, openRestrictedModal } = this.props;

    if (!accessPremium) {
      openRestrictedModal('search_law');
    } else {
      doctrineAnalytics('Search LawClicked', makeLawEventData(legislation));
    }
  };

  onSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const {
      accessPremium,
      onSelectLawArticleId,
      openRestrictedModal,
    } = this.props;

    if (!accessPremium) {
      openRestrictedModal('restrict_law_version');
    } else {
      doctrineAnalytics('Search ChangeLawVersionClicked', {
        reportOriginalQueryKey: true,
      });
      onSelectLawArticleId(value);
    }
  };

  restrictSearchToLawVersion = () => {
    const {
      accessPremium,
      onFilterOnArticle,
      filterOnSpecificArticleVersion,
      openRestrictedModal,
    } = this.props;

    if (!accessPremium) {
      openRestrictedModal('restrict_law_version');
    } else {
      doctrineAnalytics('Search RestrictLawClicked', {
        restricted: !filterOnSpecificArticleVersion,
        reportOriginalQueryKey: true,
      });
      onFilterOnArticle();
    }
  };

  loadDecisionReferences = async () => {
    const { legislation, originalQueryKey } = this.props;
    const { data } = await fetchLawCardReferenceDecisions(
      legislation.articleId,
    );
    if (data.refdecisions) {
      const refDecisionsWithUrl = data.refdecisions.map(decision => ({
        ...decision,
        url: buildUrlWithParameters(decision.referenceUrl, {
          original_query_key: originalQueryKey,
        }),
      }));
      this.setState({
        decisionsReferences: refDecisionsWithUrl,
      });
    }
  };

  render() {
    const { decisionsReferences } = this.state;
    const {
      filterOnSpecificArticleVersion,
      lawArticleId,
      legislation,
      accessPremium,
      searchQueryUrl,
    } = this.props;

    const lawUrl = accessPremium
      ? buildUrlWithParameters(`${legislation.url}?${searchQueryUrl}`, {
          source: 'law-snippet',
        })
      : undefined;
    return (
      <div className="snippet-card">
        <EntitiesCardBody>
          <LegislationCardTitleLink onClick={this.onTitleClick} href={lawUrl}>
            {legislation.title}
          </LegislationCardTitleLink>
          <Spacing />
          {legislation.otherVersions.length > 0 && (
            <>
              <LegislationSelector
                lawOtherVersions={legislation.otherVersions}
                selected={lawArticleId}
                onSelect={this.onSelect}
              />
              <LegislationRestrictor
                onCheck={this.restrictSearchToLawVersion}
                checked={!!filterOnSpecificArticleVersion}
              />
            </>
          )}
          <LegislationContent law={legislation} />

          <DecisionQpc
            source="search_law_snippet"
            articleId={legislation.articleId}
          />
          {decisionsReferences.length > 0 && (
            <DecisionReferences
              accessPremium={accessPremium}
              source="search_law_snippet"
              decisions={decisionsReferences}
            />
          )}
        </EntitiesCardBody>
      </div>
    );
  }
}

export const LegislationSnippet = Connect(
  withUser(withRestrictedModal(RawLegislationSnippet)),
);
